import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { submitContact } from "../api/contact";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../App.css";
import { useNavigate } from 'react-router-dom';

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  message: Yup.string().min(10, "Message must be at least 10 characters").required("Message is required"),
});

const Contact = (props) => {
  const [loading, setLoading] = useState(false);
  const [isDemoRequest, setIsDemoRequest] = useState(false);
  const [demoProduct, setDemoProduct] = useState("");
  const navigate = useNavigate();

  // Check if this is a demo request
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isDemo = urlParams.get('demo');
    
    if (isDemo === 'true') {
      setIsDemoRequest(true);
      
      // Get demo product info from localStorage
      const demoData = localStorage.getItem('demoRequest');
      if (demoData) {
        try {
          const parsedData = JSON.parse(demoData);
          setDemoProduct(parsedData.product);
        } catch (error) {
          console.error('Error parsing demo data:', error);
        }
      }
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      message: isDemoRequest ? `I would like to request a live demo for ${demoProduct}. Please contact me to schedule a demonstration.` : "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      try {
        // Add demo information to the submission
        const submissionData = {
          ...values,
          isDemoRequest: isDemoRequest,
          demoProduct: demoProduct,
          timestamp: new Date().toISOString()
        };
        
        const res = await submitContact(submissionData);
        toast.success(res.message || "Demo request sent successfully!", {
          position: "top-right",
          style: { zIndex: 9999 },
        });
        resetForm();
        
        // Clear demo data from localStorage
        if (isDemoRequest) {
          localStorage.removeItem('demoRequest');
        }
        // Redirect to contact success page
        setTimeout(() => {
          navigate('/contact/contact-success', { state: { name: values.name } });
        }, 500);
      } catch (err) {
        toast.error((err.error || "Failed to send demo request."), {
          position: "top-right",
          style: { zIndex: 9999 },
        });
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div id="contact">
      <ToastContainer position="top-right" style={{ zIndex: 9999 }} />
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-6 mb-4 mb-lg-0">
            <div className="section-title">
              <h2>{isDemoRequest ? "Request Live Demo" : "Get In Touch"}</h2>
              <p>
                {isDemoRequest 
                  ? `Thank you for your interest in ${demoProduct}! Please fill out the form below and we'll schedule a personalized demo for you.`
                  : "Please fill out the form below to send us an email and we will get back to you as soon as possible."}
              </p>
              {isDemoRequest && (
                <div className="demo-notice">
                  <i className="fa fa-info-circle"></i>
                  <span>Demo requests are typically responded to within 24 hours during business days.</span>
                </div>
              )}
            </div>
            <form name="sentMessage" onSubmit={formik.handleSubmit}>
              <div className="row">
                <div className="col-12 col-md-6 mb-3 mb-md-0">
                  <div className="form-group">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                      placeholder="Name"
                      {...formik.getFieldProps("name")}
                      disabled={loading}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className="form-error">{formik.errors.name}</div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      placeholder="Email"
                      {...formik.getFieldProps("email")}
                      disabled={loading}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className="form-error">{formik.errors.email}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="form-group">
                <textarea
                  name="message"
                  id="message"
                  className="form-control"
                  rows="4"
                  placeholder="Message"
                  {...formik.getFieldProps("message")}
                  disabled={loading}
                ></textarea>
                {formik.touched.message && formik.errors.message && (
                  <div className="form-error">{formik.errors.message}</div>
                )}
              </div>
              <div id="success"></div>
              <button type="submit" className="btn-custom" disabled={loading}>
                {loading ? "Sending..." : (isDemoRequest ? "Request Demo" : "Send Message")}
              </button>
            </form>
          </div>
          <div className="col-12 col-lg-5 offset-lg-1">
            <div className="contact-info">
              <div className="contact-item">
                <h3>Contact Info</h3>
                <span>
                  <i className="fa fa-map-marker" style={{ color: '#2563eb' }}></i> Address
                </span>
                <p>{props.data ? props.data.address : "loading"}</p>
              </div>
              <div className="contact-item">
                <span>
                  <i className="fa fa-phone" style={{ color: '#2563eb' }}></i> Phone
                </span>
                <p>{props.data ? props.data.phone : "loading"}</p>
              </div>
              <div className="contact-item">
                <span>
                  <i className="fa fa-envelope-o" style={{ color: '#2563eb' }}></i> Email
                </span>
                <p>{props.data ? props.data.email : "loading"}</p>
              </div>
              <div className="social">
                <ul>
                  {props.data && props.data.socialLinks && props.data.socialLinks.map((link, idx) => (
                    <li key={idx}>
                      <a href={link.url} target="_blank" rel="noopener noreferrer">
                        <i className={`fa fa-${link.icon}`}></i>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="map-heading">
          <h2>Our Location</h2>
          <p>Visit us at our office in Faisalabad</p>
        </div>
        <div className="map-container enhanced-map-container">
          <iframe 
            src="https://www.google.com/maps?q=Ali+Mall,+2nd+floor,+Susan+road,+Faisalabad,+Pakistan&output=embed" 
            title="Nexagen" 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade">
          </iframe>
        </div>
      </div>
    </div>
  );
};

export default Contact;